import { ArgoContext } from '@amzn/awsscm-argo-core/argo';
import { ApolloLink, FetchResult, HttpLink, NextLink, Operation } from '@apollo/client';
import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';

import { getEndpointForOperation } from '../apollo/operationMap';
import { RFXProperty, RFXRequestSummary, RFXRevisionedIdentifier } from '../types/RFXRequestTypes';
import { handleErrors, handleResponse } from './utils/middleware';

interface ContextualFetch {
  (input: Request | string, init?: RequestInit): Promise<Response>;
  context?: ArgoContext;
}

/**
 * Wrapper for Apollo GraphQL client, which provides fetch with argo auth, and
 * also provides a chance to handle GraphQL errors 
 */
export class ApolloClientWrapper {
  private _argoContext: ArgoContext;
  private _cache: InMemoryCache;
  private _client: ApolloClient<Object>;
  private _fetchLink: ApolloLink; 
  private _handleResponseLink: ApolloLink;
  private _handleErrorsLink: ApolloLink;

  constructor(context: ArgoContext, displayErrorLink: ApolloLink) {
    this._argoContext = context;
    const fetch: ContextualFetch = (input, init?) => {
      if (this.argoContext) return this.argoContext.fetchWithAwsAuth(input, init);
      return Promise.reject(new Error('ArgoAppContext not set'));
    };

    this._fetchLink = new ApolloLink((operation: Operation, forward: NextLink) => {
      const uri = getEndpointForOperation(operation.operationName);
      const httpLink = new HttpLink({ uri, fetch });
      return httpLink.request(operation, forward);
    });

    // Publishes metrics to PMET: https://monitorportal.amazon.com/igraph?search=Katal.Argo.SMART
    this._handleResponseLink = new ApolloLink((operation: Operation, forward: NextLink) => {
      operation.setContext({ start: performance.now() });
      return forward(operation).map((response: FetchResult) => {
        if (this.argoContext) {
          handleResponse({
            argoContext: this.argoContext,
            operation,
            response
          });
        }
        return response;
      });
    });

    // Publishes error logs in KatalLogger CloudWatch log group
    this._handleErrorsLink = onError(({ operation, graphQLErrors, networkError }: ErrorResponse) => {
      if (this.argoContext) {
        handleErrors({
          argoContext: this.argoContext,
          operation,
          graphQLErrors: graphQLErrors as GraphQLError[],
          networkError
        });
      }
    });

    this._cache = new InMemoryCache({
      dataIdFromObject(res) {
        switch (res.__typename) {
          case 'RFXRequestSummary':
            return (res as unknown as RFXRequestSummary).rfxRequestRevisionedId.id.value;
          case 'RFXRevisionedIdentifier':
            return (res as unknown as RFXRevisionedIdentifier).id.value;
          case 'RFXProperty':
            return (res as unknown as RFXProperty).id.value + (res as unknown as RFXProperty).value.valueString.value;
          default:
            return defaultDataIdFromObject(res);
        }
      },
      addTypename: false
    });

    const links = [this._handleResponseLink, this._handleErrorsLink, displayErrorLink, this._fetchLink];

    this._client = new ApolloClient({
      cache: this._cache,
      link: ApolloLink.from(links),
      queryDeduplication: false,
      // See more: https://www.apollographql.com/docs/react/development-testing/developer-tooling/#configuration
      connectToDevTools: false
    });
  }

  public get argoContext(): ArgoContext | null {
    return this._argoContext;
  }

  public get client() {
    return this._client;
  }
}
